import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import VoiceInput from './VoiceInput';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  sources?: string[]; // References to transcript IDs
  transcriptMetadata?: TranscriptMetadata[]; // Metadata for creating clickable links
}

interface TranscriptMetadata {
  id: string;
  title: string;
  created_at: string;
}

interface ChatResponse {
  content: string;
  sources?: string[];
  transcriptMetadata?: TranscriptMetadata[];
}

interface ChatProps {
  selectedTranscriptId?: string;
  onSelectTranscript?: (transcriptId: string) => void;
}

// Session management for Neural Search persistence
interface ChatSession {
  id: string;
  title: string;
  sessionType: string;
  totalMessages: number;
  lastActivityAt: string;
  createdAt: string;
  isArchived: boolean;
}

interface SessionMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  referencedTranscripts?: string[];
  contextChunks?: any[];
}

// API Response types
interface SessionsResponse {
  sessions: ChatSession[];
}

interface MessagesResponse {
  session?: ChatSession;
  messages?: SessionMessage[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface CreateSessionResponse {
  session: ChatSession;
}

interface GenerateResponse {
  sessionId: string;
  messageId: string;
  response: string;
  searchResults?: Array<{
    transcriptId: string;
    transcriptTitle?: string;
    title?: string;
    created_at?: string;
    content: string;
    score: number;
  }>;
  metadata?: {
    modelUsed: string;
    processingTimeMs: number;
    usage: any;
    estimatedCostUsd: number;
    contextChunksUsed: number;
  };
}

const Chat: React.FC<ChatProps> = ({ selectedTranscriptId, onSelectTranscript }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate session title for Neural Search
  const generateSessionTitle = useCallback((): string => {
    const now = new Date();
    const time = now.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    const date = now.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    return `Neural Search - ${date}, ${time}`;
  }, []);

  // Load or create session for Neural Search
  const initializeSession = useCallback(async () => {
    try {
      setIsInitializing(true);

      // First, try to load existing Neural Search sessions
      const sessionsResponse = await fetch('/api/ai-chat/sessions', {
        method: 'GET',
        credentials: 'include',
      });

      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json() as { sessions: ChatSession[] };
        const neuralSessions = (sessionsData.sessions || []).filter((session: ChatSession) => 
          session.sessionType === 'neural_search'
        );

        // If we have an existing neural search session, use the most recent one
        if (neuralSessions.length > 0) {
          const latestSession = neuralSessions[0]; // Sessions are ordered by last activity
          setCurrentSession(latestSession);

          // Load messages for this session
          const messagesResponse = await fetch(
            `/api/ai-chat/sessions?sessionId=${latestSession.id}&limit=50&offset=0`,
            {
              method: 'GET',
              credentials: 'include',
            }
          );

          if (messagesResponse.ok) {
            const messagesData = await messagesResponse.json() as { 
              session?: ChatSession; 
              messages?: SessionMessage[];
              pagination?: {
                total: number;
                limit: number;
                offset: number;
                hasMore: boolean;
              };
            };
            const sessionMessages = messagesData.messages || [];
            
            // Convert session messages to Chat component format
            const convertedMessages: Message[] = sessionMessages.map((msg: SessionMessage) => {
              // Extract unique transcripts from contextChunks to avoid duplicates
              const uniqueTranscripts = new Map<string, TranscriptMetadata>();
              
              msg.contextChunks?.forEach((chunk: any) => {
                const transcriptId = chunk.transcriptId;
                if (transcriptId && !uniqueTranscripts.has(transcriptId)) {
                  uniqueTranscripts.set(transcriptId, {
                    id: transcriptId,
                    title: chunk.transcriptTitle || chunk.title || 'Untitled',
                    created_at: chunk.created_at || ''
                  });
                }
              });

              return {
                id: msg.id,
                content: msg.content,
                role: msg.role,
                timestamp: new Date(msg.createdAt),
                sources: msg.referencedTranscripts,
                transcriptMetadata: Array.from(uniqueTranscripts.values())
              };
            });

            setMessages(convertedMessages);
            console.log(`[Neural Search] Restored session ${latestSession.id} with ${convertedMessages.length} messages`);
          }
        } else {
          // No existing neural search session, create a new one
          await createNewSession();
        }
      } else {
        // Failed to load sessions, create a new one
        await createNewSession();
      }
    } catch (error) {
      console.error('[Neural Search] Failed to initialize session:', error);
      // Create a new session as fallback
      await createNewSession();
    } finally {
      setIsInitializing(false);
    }
  }, [generateSessionTitle]);

  // Create new Neural Search session
  const createNewSession = useCallback(async () => {
    try {
      const sessionTitle = generateSessionTitle();
      
      const response = await fetch('/api/ai-chat/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          title: sessionTitle,
          sessionType: 'neural_search'
        }),
      });

      if (response.ok) {
        const data = await response.json() as { session: ChatSession };
        setCurrentSession(data.session);
        setMessages([]); // Start with empty messages for new session
        console.log(`[Neural Search] Created new session: ${data.session.id}`);
      } else {
        console.error('[Neural Search] Failed to create session');
      }
    } catch (error) {
      console.error('[Neural Search] Error creating session:', error);
    }
  }, [generateSessionTitle]);

  // Initialize session on component mount
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // SECURE: React component-based function to parse AI response content and make transcript titles clickable
  const parseContentWithLinks = (content: string, metadata?: TranscriptMetadata[]): React.ReactNode => {
    if (!metadata || metadata.length === 0) {
      return content;
    }

    console.log('Parsing content with transcript metadata:', {
      content: content.substring(0, 200) + '...',
      transcriptCount: metadata.length,
      transcriptTitles: metadata.map(t => ({ id: t.id, title: t.title }))
    });
    
    // Sort transcripts by title length (longest first) to avoid partial matches
    const sortedMetadata = [...metadata].sort((a, b) => b.title.length - a.title.length);
    
    let segments: React.ReactNode[] = [content];
    
    sortedMetadata.forEach((transcript, transcriptIndex) => {
      const newSegments: React.ReactNode[] = [];
      
      segments.forEach((segment, segmentIndex) => {
        if (typeof segment !== 'string') {
          newSegments.push(segment);
          return;
        }
        
        // Escape special regex characters in the title - SECURE sanitization
        const escapedTitle = transcript.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Create regex patterns for different cases
        const patterns = [
          // 1. Quoted versions with punctuation
          { regex: new RegExp(`"${escapedTitle}"([.,!?;:]*)`, 'g'), quoted: true },
          // 2. Unquoted versions with punctuation
          { regex: new RegExp(`\\b${escapedTitle}([.,!?;:]*)\\b`, 'g'), quoted: false }
        ];
        
        let hasMatches = false;
        
        patterns.forEach(pattern => {
          if (hasMatches) return;
          
          const matches = Array.from(segment.matchAll(pattern.regex));
          if (matches.length > 0) {
            hasMatches = true;
            let lastIndex = 0;
            const parts: React.ReactNode[] = [];
            
            matches.forEach((match, matchIndex) => {
              // Add text before the match
              if (match.index! > lastIndex) {
                parts.push(segment.substring(lastIndex, match.index));
              }
              
              // Add the clickable link - SECURE: React component, no HTML injection
              const transcriptTitle = pattern.quoted ? `"${transcript.title}"` : transcript.title;
              parts.push(
                <span
                  key={`transcript-${transcript.id}-${transcriptIndex}-${segmentIndex}-${matchIndex}`}
                  className="transcript-link"
                  data-transcript-id={transcript.id}
                  onClick={() => onSelectTranscript?.(transcript.id)}
                  style={{
                    color: '#7c3aed',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {transcriptTitle}
                </span>
              );
              
              // Add any punctuation that followed
              if (match[1]) {
                parts.push(match[1]);
              }
              
              lastIndex = match.index! + match[0].length;
            });
            
            // Add remaining text after last match
            if (lastIndex < segment.length) {
              parts.push(segment.substring(lastIndex));
            }
            
            newSegments.push(...parts);
          }
        });
        
        if (!hasMatches) {
          newSegments.push(segment);
        }
      });
      
      segments = newSegments;
    });
    
    return <>{segments}</>;
  };

  // Helper function to send message with specific text (for voice auto-send)
  const sendMessageWithText = async (messageText: string, inputMethod: 'voice' | 'text' = 'voice') => {
    if (!messageText || isLoading || !currentSession) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageText,
      role: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue(''); // Clear the input after sending
    setIsLoading(true);

    try {
      // Use the new AI Chat API with session support
      const response = await fetch('/api/ai-chat/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: currentSession.id,
          message: messageText,
          useClassification: true, // Enable neural search
          maxContextChunks: 5,
          rawChatMode: false
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const result = await response.json() as GenerateResponse;

      // Deduplicate transcript metadata to prevent duplicate links
      const uniqueTranscripts = new Map<string, TranscriptMetadata>();
      result.searchResults?.forEach((sr) => {
        if (!uniqueTranscripts.has(sr.transcriptId)) {
          uniqueTranscripts.set(sr.transcriptId, {
            id: sr.transcriptId,
            title: sr.transcriptTitle || sr.title || 'Untitled',
            created_at: sr.created_at || ''
          });
        }
      });

      const assistantMessage: Message = {
        id: result.messageId || (Date.now() + 1).toString(),
        content: result.response,
        role: 'assistant',
        timestamp: new Date(),
        sources: Array.from(uniqueTranscripts.keys()), // Use deduplicated transcript IDs
        transcriptMetadata: Array.from(uniqueTranscripts.values())
      };

      setMessages(prev => [...prev, assistantMessage]);
      console.log(`[Neural Search] Message sent to session ${currentSession.id}`);
    } catch (error) {
      console.error('Neural Search error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I encountered an error. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    await sendMessageWithText(inputValue, 'text');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    // Create a new session when clearing chat
    createNewSession();
  };

  const handleVoiceTranscription = (transcribedText: string, autoSend: boolean = false) => {
    if (autoSend && transcribedText.trim()) {
      // Auto-send voice transcription
      sendMessageWithText(transcribedText, 'voice');
    } else {
      // Just populate the input field
      setInputValue(transcribedText);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Add styles for clickable transcript links */}
      <style>{`
        .transcript-link {
          color: #7c3aed;
          text-decoration: underline;
          cursor: pointer;
          font-weight: 500;
        }
        .transcript-link:hover {
          color: #5b21b6;
          background-color: rgba(124, 58, 237, 0.1);
          padding: 1px 2px;
          border-radius: 3px;
        }
      `}</style>
      
      {/* Chat Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">Neural Search</h3>
        <button
          onClick={clearChat}
          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Clear Chat
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isInitializing ? (
          <div className="text-center text-gray-500 mt-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-purple mx-auto mb-4"></div>
            <p className="text-sm">Initializing Neural Search...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 mt-8">
            <div className="text-4xl mb-4">🤖</div>
            <p className="text-lg font-medium mb-2">I am Neu, your personal assistant</p>
            <p className="text-sm">Ask me anything about your transcripts!</p>
            <div className="mt-4 text-xs text-gray-400">
              <p>• "What did I say about marketing?"</p>
              <p>• "Summarize my recent recordings"</p>
              <p>• "Find mentions of project deadlines"</p>
              <p>• 🎤 Voice: 🔴 to review text, ✅ for hands-free send</p>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-accent-purple text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {message.role === 'assistant' && message.transcriptMetadata ? (
                  <div className="text-sm">
                    {parseContentWithLinks(message.content, message.transcriptMetadata)}
                  </div>
                ) : (
                  <p className="text-sm">{message.content}</p>
                )}
                {message.sources && message.sources.length > 0 && (
                  <div className="mt-2 text-xs opacity-70">
                    <span>Sources: {message.sources.length} transcript(s)</span>
                  </div>
                )}
                <div className="text-xs opacity-70 mt-1">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))
        )}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-accent-purple"></div>
                <span className="text-sm text-gray-600">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex space-x-2">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about your transcripts..."
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-purple focus:border-transparent"
            rows={2}
            disabled={isLoading}
          />
          <div className="flex flex-col space-y-2">
            <VoiceInput 
              onTranscription={handleVoiceTranscription}
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-700 transition-colors"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
